// Templates: printable pages laid out from a form's answers.
//
// A template is one US Letter page holding boxes. Each box is filled from one
// of three places:
//
//   { kind: 'field',  fieldId: 'field_1712', fieldName: 'Roof age', format: 'number' }
//   { kind: 'detail', detail: 'submitted_at', format: 'date' }   // the submission itself
//   { kind: 'text',   text: 'Inspection report' }                // what the author typed
//
// and every box says where it sits and how its text is set:
//
//   { x: 72, y: 90, w: 180, h: 18, fontSize: 11, bold: false, italic: false,
//     align: 'left', border: 'none' }
//
// Positions are PDF points (1/72 in) from the top-left corner of the sheet —
// the unit the API stores and the unit the page prints in, so the page on
// screen and the page on paper are one drawing at two scales. A question is
// referenced by id, so renaming it on the form doesn't unhook its boxes;
// `fieldName` remembers what it was called, which is how a box can say what it
// is missing once its question is deleted, and how a layout finds its
// questions again on another form.
//
// Everything here is pure. The builder's drag, resize, draw and keyboard
// operations are each one of these functions, so the awkward cases — a box
// pushed off the edge, a resize past the opposite side — are settled here and
// tested here.
import { CONTACT_PARTS } from '../contactValue';
import { getFieldDisplayName } from '../formSchemaUtils';
import { isSection, sectionFields, sectionTitle } from '../formSections';

export const PAGE_WIDTH = 612;
export const PAGE_HEIGHT = 792;
export const POINTS_PER_INCH = 72;
export const LAYOUT_VERSION = 1;

// Smallest box the builder makes: an eighth of an inch.
export const MIN_BOX = 9;
// Mirrors the ceiling on the templates blueprint.
export const MAX_BOXES = 300;
// Most printers can't reach the last quarter inch of a sheet.
export const PRINT_MARGIN = 18;

// Where "put it somewhere free" starts looking, and the room it leaves
// around the boxes already there.
const PLACE_MARGIN = 36;
const PLACE_GAP = 9;

export const GRID_OPTIONS = [
  { value: 18, label: '¼ in' },
  { value: 9, label: '⅛ in' },
  { value: 0, label: 'Off' },
];
export const DEFAULT_GRID = 18;

// --- What fills a box ------------------------------------------------------

export const DETAILS = [
  { value: 'title', label: 'Submission title' },
  { value: 'submitted_at', label: 'Date submitted' },
  { value: 'submitter', label: 'Submitted by' },
  { value: 'form_name', label: 'Form name' },
];

export const detailLabel = (detail) =>
  DETAILS.find((entry) => entry.value === detail)?.label || 'Submission detail';

export const FORMATS = [
  { value: 'text', label: 'Text', hint: 'The answer on one line' },
  { value: 'note', label: 'Note', hint: 'Wraps onto as many lines as the box holds' },
  { value: 'number', label: 'Number', hint: 'e.g. 1,234.5' },
  { value: 'percent', label: 'Percentage', hint: 'e.g. 45%' },
  { value: 'currency', label: 'Currency', hint: 'e.g. $1,234.50' },
  { value: 'date', label: 'Date', hint: 'e.g. 10/21/2026' },
  { value: 'check', label: 'Checkmark', hint: 'A tick when a chosen option is picked' },
  { value: 'photo', label: 'Photo', hint: 'The picture, fitted inside the box' },
];

export const formatLabel = (format) =>
  FORMATS.find((entry) => entry.value === format)?.label || 'Text';

// The ways each kind of question can be printed, the default first. A text
// answer can be printed as a number because inspectors type numbers into
// text questions; anything that isn't one prints as typed.
const FORMATS_BY_TYPE = {
  text: ['text', 'note', 'number', 'percent', 'currency'],
  number: ['number', 'percent', 'currency', 'text'],
  calculation: ['number', 'percent', 'currency', 'text'],
  date: ['date', 'text'],
  select: ['text', 'note', 'check'],
  contact: ['text', 'note'],
  photo: ['photo'],
};

const DETAIL_FORMATS = {
  title: ['text', 'note'],
  submitted_at: ['date'],
  submitter: ['text'],
  form_name: ['text', 'note'],
};

export const fieldFormats = (field) => FORMATS_BY_TYPE[field?.type] || FORMATS_BY_TYPE.text;

export const detailFormats = (detail) => DETAIL_FORMATS[detail] || ['text'];

// The formats on offer for a box, given the question it is filled from.
export const boxFormats = (box, field) => {
  if (box.kind === 'detail') return detailFormats(box.detail);
  if (box.kind === 'field' && field) return fieldFormats(field);
  return [];
};

export const CONTACT_PART_OPTIONS = [
  { value: 'all', label: 'Every detail' },
  ...CONTACT_PARTS.map((part) => ({ value: part.key, label: part.label })),
];

export const DATE_STYLES = [
  { value: 'short', label: '10/21/2026' },
  { value: 'medium', label: 'Oct 21, 2026' },
  { value: 'long', label: 'October 21, 2026' },
  { value: 'iso', label: '2026-10-21' },
];

export const MARKS = [
  { value: 'check', label: 'Tick ✓' },
  { value: 'x', label: 'Cross ✕' },
];

export const ALIGNMENTS = ['left', 'center', 'right'];

export const BORDERS = [
  { value: 'none', label: 'None' },
  { value: 'underline', label: 'Line' },
  { value: 'box', label: 'Box' },
];

export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
export const MIN_FONT_SIZE = 6;
export const MAX_FONT_SIZE = 72;

export const STYLE_DEFAULTS = {
  fontSize: 11,
  bold: false,
  italic: false,
  align: 'left',
  border: 'none',
};

// --- Sources: what the palette and the picker put in a box ------------------

export const fieldSource = (field) => ({ kind: 'field', field });
export const detailSource = (detail) => ({ kind: 'detail', detail });
export const TEXT_SOURCE = { kind: 'text' };

export const sourceKey = (source) => {
  if (source.kind === 'field') return `field:${source.field.id}`;
  if (source.kind === 'detail') return `detail:${source.detail}`;
  return 'text';
};

export const sourceTitle = (source) => {
  if (source.kind === 'field') return getFieldDisplayName(source.field) || 'Untitled question';
  if (source.kind === 'detail') return detailLabel(source.detail);
  return 'Text';
};

// The keys that say what fills a box. Re-pointing a box replaces these and
// keeps everything else — where it sits, how it's set, its label.
const BINDING_KEYS = ['kind', 'fieldId', 'fieldName', 'part', 'detail', 'text', 'format', 'match'];

// A box at least this tall (two lines of the default type) was drawn to hold
// more than one line.
const TALL_BOX = 36;

// The format a box starts with: the one it already has when the new source
// can print it that way, a note when it was drawn tall enough to wrap, and
// otherwise the source's usual one.
const startingFormat = (formats, current) => {
  if (formats.includes(current?.format)) return current.format;
  if (current && current.h >= TALL_BOX && formats.includes('note')) return 'note';
  return formats[0];
};

// What a source puts in a box. `current` is the box being re-pointed, or the
// blank one drawn for it.
export const bindingFor = (source, current = null) => {
  if (source.kind === 'text') {
    return { kind: 'text', text: current?.kind === 'text' ? current.text || '' : '' };
  }
  if (source.kind === 'detail') {
    return {
      kind: 'detail',
      detail: source.detail,
      format: startingFormat(detailFormats(source.detail), current),
    };
  }
  const { field } = source;
  const binding = {
    kind: 'field',
    fieldId: field.id,
    fieldName: getFieldDisplayName(field),
    format: startingFormat(fieldFormats(field), current),
  };
  if (field.type === 'contact') {
    binding.part = current?.part || 'all';
    // Every detail of a contact reads as an address block, a line apiece.
    if (!current?.format && binding.part === 'all') binding.format = 'note';
  }
  // The option that ticks a checkmark only means anything on its own question.
  if (field.type === 'select' && current?.fieldId === field.id && current.match) {
    binding.match = current.match;
  }
  return binding;
};

export const bindBox = (box, source) => {
  const kept = Object.fromEntries(
    Object.entries(box).filter(([key]) => !BINDING_KEYS.includes(key)),
  );
  return { ...kept, ...bindingFor(source, box) };
};

// A box drawn on the page before anything is chosen for it.
export const isBlankBox = (box) => box.kind === 'field' && !box.fieldId;

// How tall a box has to be to show a line of type without cutting it off,
// rounded up to the eighth of an inch.
export const lineBoxHeight = (fontSize) => Math.ceil((fontSize * 1.2) / 9) * 9;

// A new type size, with the box grown to hold a line of it if it was too
// short: type cut off at the baseline reads as a mistake. Shrinking the type
// leaves the box alone.
export const resizeType = (box, fontSize) => {
  const needed = lineBoxHeight(fontSize);
  if (box.h >= needed) return { ...box, fontSize };
  return { ...box, fontSize, ...clampRect({ ...box, h: needed }) };
};

// Changing how a box prints. A checkmark is the one format whose shape follows
// from it, so a box turned into one becomes a square at its own height with a
// frame to tick in, and a box turned back gets room for text again.
export const reformatBox = (box, format) => {
  if (format === box.format) return box;
  const next = { ...box, format };
  if (format === 'check') {
    return {
      ...next,
      ...clampRect({ ...box, w: box.h }),
      border: box.border === 'none' ? 'box' : box.border,
    };
  }
  if (box.format === 'check') {
    return { ...next, ...clampRect({ ...box, w: Math.max(box.w, defaultBoxSize(next).w) }) };
  }
  return next;
};

// --- Sizes ------------------------------------------------------------------

const DEFAULT_SIZES = {
  text: { w: 180, h: 18 },
  note: { w: 288, h: 72 },
  number: { w: 108, h: 18 },
  percent: { w: 72, h: 18 },
  currency: { w: 108, h: 18 },
  date: { w: 108, h: 18 },
  check: { w: 18, h: 18 },
  photo: { w: 180, h: 135 },
};

// How big a box starts out when it is dropped rather than drawn: room for the
// answer it usually holds, at the default type size.
export const defaultBoxSize = (binding) => {
  if (binding.kind === 'text') return { w: 216, h: 18 };
  if (binding.kind === 'field' && binding.part === 'all') return { w: 216, h: 72 };
  return DEFAULT_SIZES[binding.format] || DEFAULT_SIZES.text;
};

// --- Making boxes -----------------------------------------------------------

let boxSequence = 0;

// Unique within a layout, and proof against two boxes made in the same
// millisecond.
export const newBoxId = () => {
  boxSequence += 1;
  return `box_${Date.now()}_${boxSequence}`;
};

export const createBox = (source, rect) => {
  const binding = bindingFor(source);
  return {
    id: newBoxId(),
    ...binding,
    ...STYLE_DEFAULTS,
    // A checkmark starts out as the square it is ticked in.
    ...(binding.format === 'check' ? { border: 'box' } : {}),
    ...rect,
  };
};

export const blankBox = (rect) => ({
  id: newBoxId(),
  kind: 'field',
  fieldId: '',
  ...STYLE_DEFAULTS,
  ...rect,
});

// A copy set down a step below and to the right of the original, so it reads
// as a copy rather than vanishing on top of it.
export const copyBox = (box, offset) => ({
  ...box,
  id: newBoxId(),
  ...clampRect({ ...box, x: box.x + offset, y: box.y + offset }),
});

// --- Geometry ---------------------------------------------------------------

export const snap = (value, grid) => (grid ? Math.round(value / grid) * grid : Math.round(value));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Keep a rectangle on the sheet: at least the smallest box, at most the page,
// and pushed back inside rather than cut off when it hangs over an edge.
export const clampRect = ({ x, y, w, h }) => {
  const width = clamp(w, MIN_BOX, PAGE_WIDTH);
  const height = clamp(h, MIN_BOX, PAGE_HEIGHT);
  return {
    x: clamp(x, 0, PAGE_WIDTH - width),
    y: clamp(y, 0, PAGE_HEIGHT - height),
    w: width,
    h: height,
  };
};

export const sameRect = (a, b) => a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

// Dragging moves the box's corner to the nearest grid point and keeps its size.
export const movedRect = (start, dx, dy, grid) =>
  clampRect({ ...start, x: snap(start.x + dx, grid), y: snap(start.y + dy, grid) });

// Arrow keys move a box by an exact step, on the grid or off it.
export const nudgedRect = (rect, dx, dy) => clampRect({ ...rect, x: rect.x + dx, y: rect.y + dy });

// Resizing from a handle: the edges the handle names ('n', 'se', ...) follow
// the pointer to the grid, the others stay put, and an edge dragged past its
// opposite stops a minimum box short of it rather than turning the box inside
// out.
export const resizedRect = (start, handle, dx, dy, grid) => {
  let left = start.x;
  let top = start.y;
  let right = start.x + start.w;
  let bottom = start.y + start.h;
  if (handle.includes('w')) left = clamp(snap(left + dx, grid), 0, right - MIN_BOX);
  if (handle.includes('e')) right = clamp(snap(right + dx, grid), left + MIN_BOX, PAGE_WIDTH);
  if (handle.includes('n')) top = clamp(snap(top + dy, grid), 0, bottom - MIN_BOX);
  if (handle.includes('s')) bottom = clamp(snap(bottom + dy, grid), top + MIN_BOX, PAGE_HEIGHT);
  return { x: left, y: top, w: right - left, h: bottom - top };
};

// Pointer travel, in points, below which a press on the page is a click.
const DRAW_THRESHOLD = 4;

// The box drawn by dragging from `from` to `to`, corners on the grid. A press
// that hardly moved is a click and draws nothing; a drag along one direction
// still makes a box at least one grid step deep, since a line was meant to
// hold something.
export const drawnRect = (from, to, grid) => {
  if (Math.abs(to.x - from.x) < DRAW_THRESHOLD && Math.abs(to.y - from.y) < DRAW_THRESHOLD) {
    return null;
  }
  const least = Math.max(grid || 0, MIN_BOX);
  const left = clamp(snap(Math.min(from.x, to.x), grid), 0, PAGE_WIDTH - least);
  const top = clamp(snap(Math.min(from.y, to.y), grid), 0, PAGE_HEIGHT - least);
  const right = clamp(snap(Math.max(from.x, to.x), grid), left + least, PAGE_WIDTH);
  const bottom = clamp(snap(Math.max(from.y, to.y), grid), top + least, PAGE_HEIGHT);
  return { x: left, y: top, w: right - left, h: bottom - top };
};

// Where a box carried in from the palette lands: the pointer holds it a little
// in from its top-left corner, the way a card is held, and the corner settles
// on the grid.
export const rectAtPoint = (point, size, grid) =>
  clampRect({
    ...size,
    x: snap(point.x - 6, grid),
    y: snap(point.y - Math.min(size.h / 2, 9), grid),
  });

const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

// The first open spot for a box of `size`, working down the page a column at a
// time from the top-left, so boxes added by clicking stack into a list the way
// they'd be read. Falls back to the top-left corner on a full page.
export const findFreeSpot = (boxes, size, grid) => {
  const step = grid || PLACE_GAP;
  const taken = boxes.map((box) => ({
    x: box.x - PLACE_GAP,
    y: box.y - PLACE_GAP,
    w: box.w + PLACE_GAP * 2,
    h: box.h + PLACE_GAP * 2,
  }));
  for (let x = PLACE_MARGIN; x + size.w <= PAGE_WIDTH - PLACE_MARGIN; x += step) {
    for (let y = PLACE_MARGIN; y + size.h <= PAGE_HEIGHT - PLACE_MARGIN; y += step) {
      const candidate = { x, y, w: size.w, h: size.h };
      if (!taken.some((box) => overlaps(candidate, box))) return candidate;
    }
  }
  return clampRect({ ...size, x: PLACE_MARGIN, y: PLACE_MARGIN });
};

export const toInches = (points) => Math.round((points / POINTS_PER_INCH) * 100) / 100;
export const fromInches = (inches) => Math.round(inches * POINTS_PER_INCH);

// --- Layouts ----------------------------------------------------------------

export const emptyLayout = () => ({ version: LAYOUT_VERSION, page: { size: 'letter' }, boxes: [] });

export const findBox = (layout, id) => layout.boxes.find((box) => box.id === id) || null;

export const addBox = (layout, box) => ({ ...layout, boxes: [...layout.boxes, box] });

// Hands back the same layout when the patch changes nothing, so a no-op edit
// never becomes a step to undo.
export const updateBox = (layout, id, patch) => {
  let changed = false;
  const boxes = layout.boxes.map((box) => {
    if (box.id !== id) return box;
    const next = typeof patch === 'function' ? patch(box) : { ...box, ...patch };
    if (Object.keys({ ...box, ...next }).every((key) => box[key] === next[key])) return box;
    changed = true;
    return next;
  });
  return changed ? { ...layout, boxes } : layout;
};

export const removeBox = (layout, id) => {
  const boxes = layout.boxes.filter((box) => box.id !== id);
  return boxes.length === layout.boxes.length ? layout : { ...layout, boxes };
};

const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
const finite = (value, fallback) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

// Coerce a stored box into the shape the builder draws. Anything the API
// let through is kept; anything missing gets its default.
export const normalizeBox = (raw) => {
  const kind = pick(raw.kind, ['field', 'detail', 'text'], 'text');
  const box = {
    ...raw,
    id: typeof raw.id === 'string' && raw.id ? raw.id : newBoxId(),
    kind,
    ...clampRect({
      x: finite(raw.x, 0),
      y: finite(raw.y, 0),
      w: finite(raw.w, DEFAULT_SIZES.text.w),
      h: finite(raw.h, DEFAULT_SIZES.text.h),
    }),
    fontSize: clamp(finite(raw.fontSize, STYLE_DEFAULTS.fontSize), MIN_FONT_SIZE, MAX_FONT_SIZE),
    bold: raw.bold === true,
    italic: raw.italic === true,
    align: pick(raw.align, ALIGNMENTS, STYLE_DEFAULTS.align),
    border: pick(raw.border, BORDERS.map((entry) => entry.value), STYLE_DEFAULTS.border),
  };
  if (kind === 'field') box.fieldId = typeof raw.fieldId === 'string' ? raw.fieldId : '';
  if (kind === 'text') box.text = typeof raw.text === 'string' ? raw.text : '';
  return box;
};

export const normalizeLayout = (raw) => {
  const boxes = Array.isArray(raw?.boxes) ? raw.boxes : [];
  return {
    version: LAYOUT_VERSION,
    page: { size: 'letter' },
    boxes: boxes.filter((box) => box && typeof box === 'object').map(normalizeBox),
  };
};

// --- Reading a layout against its form --------------------------------------

// What, if anything, stops a box printing what its author meant. Nothing here
// blocks a save — a half-finished template is still worth keeping — but the
// builder marks each one on the page.
export const boxIssue = (box, fieldsById) => {
  if (box.kind !== 'field') return null;
  if (!box.fieldId) return 'Nothing is chosen for this box yet.';
  const field = fieldsById.get(box.fieldId);
  if (!field) {
    return box.fieldName
      ? `“${box.fieldName}” is no longer on the form.`
      : 'The question this box was filled from is no longer on the form.';
  }
  if (!fieldFormats(field).includes(box.format)) {
    return `This question can’t be printed as ${formatLabel(box.format).toLowerCase()} any more. Pick how to show it.`;
  }
  if (box.format === 'check') {
    if (!box.match) return 'Pick the answer that ticks this box.';
    if (!(field.options || []).includes(box.match)) {
      return `“${box.match}” is no longer one of this question’s options.`;
    }
  }
  return null;
};

export const layoutIssues = (layout, fields) => {
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  return layout.boxes
    .map((box) => ({ box, message: boxIssue(box, fieldsById) }))
    .filter((issue) => issue.message);
};

// How many boxes each source fills, keyed by sourceKey — for the palette to
// show what is already on the page.
export const placedCounts = (layout) => {
  const counts = new Map();
  layout.boxes.forEach((box) => {
    let key = null;
    if (box.kind === 'field' && box.fieldId) key = `field:${box.fieldId}`;
    if (box.kind === 'detail') key = `detail:${box.detail}`;
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

// Bring each box's remembered question name up to date, so a question renamed
// on the form since its box was placed is known by its current name.
export const refreshFieldNames = (layout, fields) => {
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  let changed = false;
  const boxes = layout.boxes.map((box) => {
    const field = box.kind === 'field' ? fieldsById.get(box.fieldId) : null;
    const name = field ? getFieldDisplayName(field) : null;
    if (!name || name === box.fieldName) return box;
    changed = true;
    return { ...box, fieldName: name };
  });
  return changed ? { ...layout, boxes } : layout;
};

const nameKey = (name) => (typeof name === 'string' ? name.trim().toLowerCase() : '');

// Point a layout at another form's questions. A box keeps its question when
// the form has it, finds one of the same name when it doesn't, and is left
// for the author otherwise. `kept`, `matched` and `lost` count the three.
export const rebindLayout = (layout, fields) => {
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  const fieldsByName = new Map();
  fields.forEach((field) => {
    const key = nameKey(getFieldDisplayName(field));
    if (key && !fieldsByName.has(key)) fieldsByName.set(key, field);
  });
  let kept = 0;
  let matched = 0;
  let lost = 0;
  const boxes = layout.boxes.map((box) => {
    if (box.kind !== 'field' || !box.fieldId) return box;
    if (fieldsById.has(box.fieldId)) {
      kept += 1;
      return box;
    }
    const twin = fieldsByName.get(nameKey(box.fieldName));
    if (!twin) {
      lost += 1;
      return box;
    }
    matched += 1;
    return bindBox(box, fieldSource(twin));
  });
  return { layout: matched ? { ...layout, boxes } : layout, kept, matched, lost };
};

// --- The questions, as the palette lists them -------------------------------

// The form's questions in the order they're answered, in runs that share a
// section — what the palette and the picker list under section headings. A
// question at the top level of the form has no heading.
export const groupFieldsBySection = (entries = []) => {
  const groups = [];
  const walk = (list, path) => {
    list.forEach((entry) => {
      if (isSection(entry)) {
        walk(sectionFields(entry), [...path, sectionTitle(entry)]);
        return;
      }
      const title = path.join(' › ');
      const last = groups[groups.length - 1];
      if (last && last.title === title) last.fields.push(entry);
      else groups.push({ key: `${groups.length}-${title}`, title, fields: [entry] });
    });
  };
  walk(Array.isArray(entries) ? entries : [], []);
  return groups;
};

export const filterFieldGroups = (groups, query) => {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return groups;
  const matches = (field, title) =>
    [getFieldDisplayName(field), field.label, field.name, title].some((text) =>
      (text || '').toLowerCase().includes(needle));
  return groups
    .map((group) => ({ ...group, fields: group.fields.filter((field) => matches(field, group.title)) }))
    .filter((group) => group.fields.length > 0);
};
